import _ from 'lodash';

/**
 * [decodeHtmlData is to decode the wysiwyg content that been rawurlencoded in Matrix.
 * The "\" is encoded/decoded differently in PHP and Javascript.]
 * @author cyan@squiz.net
 *
 * @param  {[string]} htmlData [wysiwyg content with rawurlencode modifier ]
 * @return {[string]}          [decoded wysiwyg content]
 */
function decodeHtmlData(encodedData) {

    return decodeURIComponent(encodedData).replace(/\\(?!\\)/g, '');
}

/**
 * clean array of strings,
 * do lowercase, remove special characters, replace empty space with '_'
 * @author cyan@squiz.net
 *
 * @param [array] arr       [could be array of degree names / discipline labels / etc]
 * @return [array] result   [array of clearned degree names / discipline labels / etc]
 */
function cleanArrToCompare(arr) {
    let reg = new RegExp(/[^\w]+/g),
        result = arr.length === 0 ? [] : arr.map(function(item) {
            return item.trim().toLowerCase().replace(reg, '_');
        });

    return result;
}

/* Check if this unit availability matches
 * the selected offerings
 *
 * @param [string] itemClassification
 *        [array]  currClassification
 *        [bool]   allowEmptySearch
 *
 * @return [bool]  result
 */
function hasOffering(itemOffering = '', currOffering = [], allowEmptySearch = true) {
    var result = false,
        cleanedItemOfferingArr = [];

    // for empty search
    if (currOffering.length === 0 && allowEmptySearch) {
        return true;
    }

    cleanedItemOfferingArr = itemOffering.split(';').map(function(item) {
        return item.trim();
    });

    if (_.intersection(currOffering, cleanedItemOfferingArr).length > 0) {
        result = true;
    }

    return result;
}

/* Check if this degree classification matches
 * the one passed to App options from matrix
 *
 * @param [string] itemClassification
 *        [string] currClassification
 *        [bool]   allowEmptySearch
 *
 * @return [bool]  result
 */
function hasClassification(itemClassification = '', currClassification = '', allowEmptySearch = true) {
    var itemClassArray = [],
        itemClassArrayTemp = [],
        result = false;

    // for empty search
    if (currClassification.length === 0 && allowEmptySearch) {
        return true;
    }

    if (currClassification == 'Intermediate' && itemClassification == 'Critical Content') {
        return true;
    }

    itemClassArrayTemp = itemClassification.toLowerCase().trim().split(';');
    for (var i = 0; i <= itemClassArrayTemp.length; i++) {
        if (itemClassArrayTemp[i]) {
            itemClassArray[i] = itemClassArrayTemp[i].trim();
        }
    }
    if (itemClassArray.indexOf(currClassification.toLowerCase()) >= 0) {
        return true;
    }

    return result;
}

/* Check if this degree has enough units or not
 *
 * @param [number] itemUnitCount
 *        [number] limit
 *
 * @return [bool] result
 */
function hasEnoughUnits(itemUnitsCount, limit = 0) {
    let limitNum = parseInt(limit, 10),
        itemUnitsCountNum = parseInt(itemUnitsCount, 10),
        result = false;

    // for empty search
    if (isNaN(limitNum) || limitNum === 0) {
        return true;
    }

    if (itemUnitsCountNum >= limitNum) {
        result = true;
    }

    return result;
}

/* Check if a single degree name matches the search term
 * @param [string] itemName
 *        [string] term
 *        [bool]   allowEmptySearch
 *
 * @return [bool]  result
 */
function hasTerm(itemName, term, allowEmptySearch = true) {
    let regStr = term.length === 0 ? '.*' : '(' + term + ')',
        reg = new RegExp(regStr, 'i'),
        result = false;

    // for empty search
    if (term.length === 0 && allowEmptySearch) {
        return true;
    }

    if (reg.test(itemName)) {
        result = true;
    }

    return result;
}

/* Check if any disciplines the degree belongs to
 * matches the current selected disciplines
 *
 * @param [string] itemDisciplines (multiple disciplines joint with ';')
 *        [array]  currDisciplines (current selected disciplines array)
 *        [bool]   allowEmptySearch
 *
 * @return [bool]  result
 */
function hasDisciplines(itemDisciplines = '', currDisciplines = [], allowEmptySearch = true) {
    var itemDiscArr = itemDisciplines.toLowerCase().split(';'),
        result = false,
        same = _.intersection(itemDiscArr, currDisciplines);

    // for empty search
    if (currDisciplines.length === 0 && allowEmptySearch) {
        return true;
    }

    if (same.length > 0) {
        result = true;
    }

    return result;
}

/* compare disciplines with cleaned arrays (for All Unit page to use)
 *
 * @param [string] itemDisciplines (multiple disciplines joint with ';')
 *        [array]  currDisciplines (current selected disciplines array)
 *        [bool]   allowEmptySearch
 *
 * @return [bool]  result
 */
function hasDisciplinesPlus(itemDisciplines = '', currDisciplines = [], allowEmptySearch = true) {
    var cleanedItemDiscArr = this._cleanArrToCompare(itemDisciplines.split(';')),
        cleanedCurrDiscArr = this._cleanArrToCompare(currDisciplines),
        result = false,
        same = _.intersection(cleanedItemDiscArr, cleanedCurrDiscArr);

    // for empty search
    if (currDisciplines.length === 0 && allowEmptySearch) {
        return true;
    }

    if (same.length > 0) {
        result = true;
    }

    return result;
}

/* CHeck if the unit belongs to one of the given degrees
 *
 * @param [string] itemDegrees (multiple degree names joint with ';')
 *        [array]  currDegrees (degrees to match)
 *        [bool]   allowEmptySearch
 *
 * @return [bool]  result
 */
function hasDegrees(itemDegrees = '', currDegrees, allowEmptySearch = true) {
    let cleanedItemDegrees = this._cleanArrToCompare(itemDegrees.split(';')),
        cleanedCurrDegrees = this._cleanArrToCompare(currDegrees),
        result = false,
        same = _.intersection(cleanedItemDegrees, cleanedCurrDegrees);

    // for empty Search
    if (currDegrees.length === 0 && allowEmptySearch) {
        return true;
    }

    if (same.length > 0) {
        result = true;
    }

    return result;
}

/* Divide the disciplines array for two columns
 *
 * @param [array] disciplinesArray
 *
 * @return [object] chunkedDisciplines {firstColumn, secondColumn}
 */
function divDisciplines(disciplines = []) {
    var newDisciplines = {},
        len = disciplines.length,
        chunckedDisc = _.chunk(disciplines, Math.ceil(len / 2));

    newDisciplines.firstColumn = chunckedDisc[0] || [];
    newDisciplines.secondColumn = chunckedDisc[1] || [];

    return newDisciplines;
}

/* Divide the degrees to two categories: postgraduate and undergraduateData
 * (change Array to Object)
 *
 * @param [array]   degrees
 * @return [object] degrees { postgraduate:{}, undergraduate:{} }
 */
function catDegrees(degrees) {
    let newDegrees = {},
        postgraduateData = [],
        undergraduateData = [];

    for (let i = 0, len = Object.keys(degrees).length; i < len; i++) {
        let item = degrees[i];

        if (item.degree_type === 'postgraduate') {
            postgraduateData.push(item);
        }

        if (item.degree_type === 'undergraduate') {
            undergraduateData.push(item);
        }
    }

    newDegrees.postgraduate = postgraduateData.concat();
    newDegrees.undergraduate = undergraduateData.concat();

    return newDegrees;
}


/* Filter the degrees
 *
 * @param [object] options {degree, term, currDisciplines, currClassification, unitsCount}
 * @return [object] categoriesd degree
 */
function filter(customOptions) {
    const defaultOptions = {
        degrees: [],
        term: '',
        currClassification: '',
        currDisciplines: [],
        unitsCount: 0
    };

    let result = [],
        options = _.merge({}, defaultOptions, customOptions);

    for (let i = 0, len = options.degrees.length; i < len; i++) {
        let item = options.degrees[i],

            itemName = item.name || '',
            itemDisciplines = item.discipline_values || '',
            itemClassification = item.classification || '',
            itemUnitsCount = item.units_count || 0,

            hasTerm = this._hasTerm(itemName, options.term),
            hasDisciplines = this._hasDisciplines(itemDisciplines, options.currDisciplines),
            hasClassification = this._hasClassification(itemClassification, options.currClassification),
            hasEnoughUnits = this._hasEnoughUnits(itemUnitsCount, options.unitsCount);

        if (options.currClassification == 'Critical Content' || options.currClassification == 'Critical' || options.currClassification == '') {
            if ((hasDisciplines && hasTerm && hasEnoughUnits)) {
                result.push(item);
            }
        } else {
            if ((hasClassification && hasDisciplines && hasTerm && hasEnoughUnits)) {
                result.push(item);
            }
        }
    }

    return result;
}

/* Filter disciplines
 *
 */
function filterDisciplines(customOptions) {
    const defaultOptions = {
        disciplines: [],
        term: '',
        currClassification: ''
    };

    let result = [],
        options = _.merge({}, defaultOptions, customOptions);

    for (let i = 0, len = options.disciplines.length; i < len; i++) {
        let item = options.disciplines[i],
            itemLabel = item.label || '',
            hasTerm = this._hasTerm(itemLabel, options.term);

        if (hasTerm) {
            result.push(item);
        }
    }

    return result;
}

/* Filter all units
 *
 */
function filterAllUnits(customOptions) {
    const defaultOptions = {
        units: [],
        currDisciplines: [],
        currDegrees: [],
        currClassification: [],
        currOffering: []
    };

    let result = [],
        options = _.merge({}, defaultOptions, customOptions);
    //degreesForMatch = options.currDegrees.length > 0 ? options.currDegrees : options.avaiDegrees,
    //degreesNameArr = options.currDegrees.map(function(degree) { return degree.name; });

    for (let i = 0, len = options.units.length; i < len; i++) {
        let item = options.units[i],
            itemDisciplines = item.Discipline || '',
            hasDisciplines = this._hasDisciplinesPlus(itemDisciplines, options.currDisciplines),

            itemDegrees = item.degrees || '',
            hasDegrees = this._hasDegrees(itemDegrees, options.currDegrees),

            itemClass = item.Classification || '',
            hasClass = this._hasClassification(itemClass, options.currClassification),

            itemOffering = item.Availability || '',
            hasOffering = this._hasOffering(itemOffering, options.currOffering);

        if (hasDisciplines && hasDegrees && hasClass && hasOffering) {
            result.push(item);
        }
    }

    return result;
}

function filterDisplinesByPath(units, disciplines, allDegrees, currClassification) {
    if (currClassification != 'Advanced' && currClassification != 'Fundamental' && currClassification != 'Intermediate') {
        return disciplines;
    }

    if (!units) {
        return disciplines;
    }

    var allUnits = units.units;
    var temp = [];
    for (var i in allUnits) {
        if (allUnits[i].Classification === undefined) {
            continue;
        }

        if(allUnits[i].Classification == 'Critical Content' && currClassification == 'Intermediate'){
            var unitDisciplines = allUnits[i].Discipline.split(';');

            var k = unitDisciplines.length;
            while (k--) {
                if (unitDisciplines[k].length < 1) {
                    continue;
                }

                var unitDiscipline = unitDisciplines[k].trim();

                if (temp.hasOwnProperty(unitDiscipline)) {
                    temp[unitDiscipline]++;
                } else {
                    temp[unitDiscipline] = 1;
                }
            }
        }
        else if (allUnits[i].Classification.indexOf(currClassification) >= 0) {
            var unitDisciplines = allUnits[i].Discipline.split(';');

            var k = unitDisciplines.length;
            while (k--) {
                if (unitDisciplines[k].length < 1) {
                    continue;
                }

                var unitDiscipline = unitDisciplines[k].trim();

                if (temp.hasOwnProperty(unitDiscipline)) {
                    temp[unitDiscipline]++;
                } else {
                    temp[unitDiscipline] = 1;
                }
            }
        }
    }

    var j = disciplines.length;
    var temp1 = [];
    while (j--) {
        var label = disciplines[j].label;

        if (temp.hasOwnProperty(label)) {
            disciplines[j].units_count = temp[label];
            temp1.push(disciplines[j]);
        }
    }

    temp = [];

    for (var n in allDegrees) {
        var degree = allDegrees[n];

        if (degree.classification === undefined) {
            continue;
        }

        if (degree.classification == 'Critical Content' && currClassification == 'Intermediate') {
            var degreeDisciplines = degree.discipline_labels.split(';');

            var m = degreeDisciplines.length;
            while (m--) {
                if (degreeDisciplines[m].length < 1) {
                    continue;
                }

                var degreeDiscipline = degreeDisciplines[m].trim();

                if (temp.hasOwnProperty(degreeDiscipline)) {
                    temp[degreeDiscipline]++;
                } else {
                    temp[degreeDiscipline] = 1;
                }
            }
        }else if (degree.classification.indexOf(currClassification) >= 0) {
            var degreeDisciplines = degree.discipline_labels.split(';');

            var m = degreeDisciplines.length;
            while (m--) {
                if (degreeDisciplines[m].length < 1) {
                    continue;
                }

                var degreeDiscipline = degreeDisciplines[m].trim();

                if (temp.hasOwnProperty(degreeDiscipline)) {
                    temp[degreeDiscipline]++;
                } else {
                    temp[degreeDiscipline] = 1;
                }
            }
        }
    }

    disciplines = temp1.slice();

    var p = disciplines.length;
    var temp2 = [];
    while (p--) {
        var label = disciplines[p].label;

        if (temp.hasOwnProperty(label)) {
            disciplines[p].units_count = temp[label];
            temp2.push(disciplines[p]);
        }
    }

    disciplines = temp2.slice();
    return disciplines;
}

function getPreSelected(allDegrees, allDisciplines) {
    let preSelOptions = [];

    if (window === undefined || window.location === undefined || window.location.search === undefined) {
        preSelOptions.classification = '';
        preSelOptions.currDegrees = [];
        preSelOptions.currDisciplines = [];

        return preSelOptions;
    }

    let preSelected = window.location.search.substring(1);
    if (preSelected.length < 0) {
        preSelOptions.classification = '';
        preSelOptions.currDegrees = [];
        preSelOptions.currDisciplines = [];

        return preSelOptions;
    }

    let preSelectedCriterias = preSelected.split("&");

    let selectedClassification = '',
        selectedDegress = [],
        selectedDisciplines = [],
        temp = [];


    for (let i = 0; i < preSelectedCriterias.length; i++) {
        //for (let i in preSelectedCriterias){
        if (preSelectedCriterias[i].length < 0 && preSelectedCriterias[i] === undefined) {
            continue;
        }

        let preSelectedCriteria = preSelectedCriterias[i].split("=");
        let criteria = preSelectedCriteria[0];
        let criteriaValue = preSelectedCriteria[1];

        if (criteria == 'classification') {
            selectedClassification = firstLetterCal(criteriaValue);
            if (selectedClassification != 'Advanced' && selectedClassification != 'Fundamental' && selectedClassification != 'Intermediate') {
                selectedClassification = '';
            }
        } else if (criteria == 'degree_name') {
            if (criteriaValue.indexOf(';') > 0) {
                var preDegrees = decodeURI(criteriaValue).split(";");
                var preDegree = '';
                for (let j = 0; j < preDegrees.length; j++) {
                    //for(let j in preDegrees) {
                    preDegree = preDegrees[j];
                    selectedDegress.push(preDegree);
                    temp.push(getDisciplinesByDegree(preDegree, allDegrees, allDisciplines));
                }

            } else {
                preDegree = decodeURI(criteriaValue);
                selectedDegress.push(preDegree);
                selectedDisciplines = getDisciplinesByDegree(preDegree, allDegrees, allDisciplines);
            }
        }
    }

    if (temp.length > 0) {
        let mergedTemp = [];
        for (var x in temp) {
            mergedTemp = merge_array(mergedTemp, temp[x]);
        }

        selectedDisciplines = mergedTemp;
    }

    preSelOptions.classification = selectedClassification
    preSelOptions.currDegrees = selectedDegress;
    preSelOptions.currDisciplines = selectedDisciplines;

    return preSelOptions;
}

function firstLetterCal(str) {
    return str.charAt(0).toUpperCase() + str.substr(1).toLowerCase();
}

function merge_array(array1, array2) {
    var result_array = [];
    var arr = array1.concat(array2);
    var len = arr.length;
    var assoc = {};

    while (len--) {
        var item = arr[len];

        if (!assoc[item]) {
            result_array.unshift(item);
            assoc[item] = true;
        }
    }

    return result_array;
}

function getDisciplinesByDegree(preSelectedDegree, allDegrees, allDisciplines) {
    let degreeExist = false;
    let disciplinesValue = [];

    for (var i = 0; i < allDegrees.length; i++) {
        //for(var i in allDegrees) {
        if (allDegrees[i].name == preSelectedDegree) {
            let disciplinesName = allDegrees[i].discipline_labels;
            degreeExist = true;

            let disciplines = disciplinesName.split(";");
            for (var j in disciplines) {
                if (disciplines[j].length < 1) {
                    continue;
                }

                var discipline = disciplines[j].trim();

                disciplinesValue.push(getDisciplineValue(discipline, allDisciplines));
            }
            break;
        }
    }

    if (!degreeExist) {
        //
    }

    return disciplinesValue;

}

function getDisciplineValue(disciplineName, allDisciplines) {
    for (var i in allDisciplines) {
        if (allDisciplines[i].label == disciplineName) {
            return allDisciplines[i].value;
        }
    }

    return null;
}

export {
    decodeHtmlData,
    cleanArrToCompare,
    hasOffering,
    hasClassification,
    hasEnoughUnits,
    hasTerm,
    hasDisciplines,
    hasDisciplinesPlus,
    hasDegrees,
    catDegrees,
    divDisciplines,
    filter,
    filterDisciplines,
    filterAllUnits,
    filterDisplinesByPath,
    getPreSelected
};